import { FC, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

export interface GroupNameDialogProps {
  visible: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  parentName?: string | null;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void> | void;
}

export const GroupNameDialog: FC<GroupNameDialogProps> = ({ visible, mode, initialName, parentName, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setName(initialName ?? '');
    }
  }, [visible, initialName]);

  const isCreate = mode === 'create';
  const trimmedName = name.trim();

  const handleSubmit = async () => {
    if (!trimmedName) return;
    setIsSaving(true);
    try {
      await onSubmit(trimmedName);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const header = isCreate
    ? parentName
      ? `New Sub-Group in "${parentName}"`
      : 'New Configuration Group'
    : 'Rename Group';

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={
        <div className="flex align-items-center gap-2">
          <i className="pi pi-sitemap" style={{ color: '#3b82f6' }} />
          <span>{header}</span>
        </div>
      }
      modal
      style={{ width: '480px', maxWidth: '90vw' }}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label="Cancel" className="p-button-text" onClick={onClose} disabled={isSaving} />
          <Button
            label={isCreate ? 'Create & set logic' : 'Save'}
            icon="pi pi-check"
            onClick={handleSubmit}
            disabled={!trimmedName || isSaving}
            loading={isSaving}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-2">
        <label htmlFor="group-name" className="font-medium">
          Group name<span className="text-red-500">*</span>
        </label>
        <InputText
          id="group-name"
          value={name}
          autoFocus
          placeholder="e.g. Branch 110"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
          className="w-full border-1 border-solid border-gray-300"
        />
        {isCreate && (
          <small className="text-gray-600">
            After creating, you&apos;ll set the group&apos;s logic. Configurations in this group are evaluated only when
            that logic is true.
          </small>
        )}
      </div>
    </Dialog>
  );
};
